import { useState, useCallback, useMemo, useRef } from "react";

import {
  readLiveBoard, writeLiveBoard, clearLiveBoard,
  saleFromPlayer, boardTotals, soldNames, suggestedTeamNames, writeSavedTeamNames,
  buildLiveTeams, liveRosterSlots, positionsForRosterSize, roomNeeds,
  teamIsFull, boardIsComplete, nextOpenSeat, prevOpenSeat,
  readLiveBoardHistory, writeLiveBoardHistory, clearLiveBoardHistory,
  historySnapshot, applyHistorySnapshot, droppedTailSale, HISTORY_DEPTH,
} from '../utils/liveBoard';
import { buildRosterSlots } from '../utils/rosterSlotFill';
import { publishBoard, newShareId } from '../utils/liveBoardPublish';
import { captureLiveBoardStarted, captureLiveBoardCompleted, captureLiveBoardHalfway } from '../utils/liveBoardAnalytics';
import { logLiveBoard } from '../utils/liveBoardDraftLog';

/**
 * useLiveBoard — state for the Live Board surface.
 *
 * ⚠️ THIS HOOK IMPORTS NOTHING FROM useMockDraft, AND MUST NOT.
 * It runs during a real auction, once a year, with eleven other people
 * watching. No shared mutable state means the mock draft's freeze class of bug
 * is structurally unable to reach this screen. It also owns its own
 * localStorage, per the project's per-hook persistence rule — there is no
 * shared persistence layer to break.
 *
 * NO TIMERS, NO INTERVALS, NO ASYNC in the entry path. The only effectful
 * thing a commit does is one synchronous localStorage write.
 *
 * 🔴 WHY PERSISTENCE IS NOT AN EFFECT. Every write happens INSIDE the handler
 * that changed the board, before React has re-rendered anything. An effect
 * would run after paint — so a refresh landing between the paint and the
 * effect loses the pick that is already on screen, which is precisely the
 * report this design exists to prevent. Debouncing loses more.
 */
// Roster size from the league's own slot definition, so a 20-man or SFLEX
// league prefills correctly. buildRosterSlots is the same derivation the rest
// of the app uses; the fallback is the calibrated 16.
function rosterSizeOf(positions) {
  try {
    const n = positions ? buildRosterSlots(positions).length : 0;
    return n > 0 ? n : 16;
  } catch {
    return 16;
  }
}

/**
 * The board's two analytics milestones, fired on a TRANSITION rather than on a
 * write. See the header of liveBoardAnalytics.js for why they exist at all.
 *
 * 🔴 CALLED ONLY FROM commitBoard's LOGGING BRANCH, which is already gated on
 * `next.sales !== prev.sales`. That gate is doing load-bearing work here and
 * not just saving a call: a nomination, a rename, a clock change and a pointer
 * step all rebuild the board without touching `sales`, and every one of them
 * would otherwise re-evaluate a threshold that had not moved.
 *
 * ⚠️ WHY A TRANSITION AND NOT A FLAG. A board is resumed from localStorage on
 * every reload, and a completed one can still be edited (a corrected price is
 * a new `sales` array). A stored "already fired" bit would have to survive
 * that resume, and a ref would not survive it at all. Comparing prev to next
 * needs neither: re-announcing a milestone would require the fill count to
 * fall back below the threshold and climb through it a second time, which is
 * exactly the case where a second event IS the truth.
 *
 * ⚠️ NEVER THROWS. It runs on the entry path — Ben's hands, 160 picks, eleven
 * people waiting — and the same rule as logLiveBoard applies: telemetry that
 * can cost a keystroke is worse than no telemetry. boardTotals walks the
 * sales array, so the whole body is wrapped rather than trusted.
 */
function captureMilestones(prev, next) {
  try {
    if (!next?.shareId) return;
    const shape = {
      teams: Array.isArray(next.teamNames) ? next.teamNames.length : null,
      budget: next.budget ?? null,
      rosterSize: next.rosterSize ?? null,
      isSflex: (next.positions?.SFLEX || 0) > 0,
      sales: Array.isArray(next.sales) ? next.sales.length : null,
      startedAt: next.startedAt,
    };

    // Completed first: a board that fills its last slot has also crossed
    // halfway, and on a one-slot-per-team room the two can land on the same
    // sale. Both are true and both fire; the order is only so the read of a
    // completed board is not preceded by a stale-looking halfway.
    if (!boardIsComplete(prev) && boardIsComplete(next)) {
      captureLiveBoardCompleted(shape);
    }

    // Halfway is measured in FILLED SLOTS, not sales — they are the same
    // number today, but slots is what the threshold means and what stays
    // correct if a sale ever fills more than one.
    const half = t => {
      const { filled, totalSlots } = boardTotals(t);
      return totalSlots > 0 && filled * 2 >= totalSlots;
    };
    if (!half(prev) && half(next)) {
      captureLiveBoardHalfway(shape);
    }
  } catch {
    /* analytics must never reach the entry path */
  }
}

export default function useLiveBoard(activeLeagueName, settings) {
  // The board, or null when no auction has been set up on this device yet.
  // Initialised straight from localStorage so a refresh mid-auction comes back
  // with the picks already in hand rather than flashing an empty screen.
  const [board, setBoard] = useState(() => readLiveBoard(activeLeagueName));

  // 🔴 RESTORE LOUDLY. A silent restore is how you get a duplicate-entry
  // report: he cannot tell "resumed with 47 picks" from "empty board" until he
  // has typed pick 48 twice. Set once at mount, dismissed by the user.
  const [restoredCount, setRestoredCount] = useState(() => {
    const existing = readLiveBoard(activeLeagueName);
    return existing && existing.sales.length > 0 ? existing.sales.length : 0;
  });

  const dismissRestored = useCallback(() => setRestoredCount(0), []);

  // Set when a write fails (quota, private mode, a full disk). Surfaced as a
  // persistent warning rather than a toast — if the board is not saving, he
  // needs to know for the rest of the night, not for four seconds.
  const [storageFailed, setStorageFailed] = useState(false);

  // Whether the spectator feed is keeping up. NOT a blocker: null = nothing
  // published yet, true = synced, false = the last POST did not land. The
  // person entering picks is never stopped by this.
  const [syncOk, setSyncOk] = useState(null);

  /**
   * ══════════════════════════════════════════════════════════════════════
   * 🔴 UNDO IS A STACK OF BOARD STATES, N DEEP, OVER EVERY VERB.
   *
   * Ben: "When I hit undo I expect that it removes the last sold player and
   * moves the nomination back… If we undo twice, it should undo the last two
   * player sales and move the nomination back two spots accordingly."
   *
   * ⚠️ THIS REPLACED `autoAdvancedRef`, WHICH IS DELETED. That ref existed to
   * record the ONE pointer move `commitSale` makes so `undoLast` could reverse
   * it — a careful, correct, single-purpose mechanism that a snapshot makes
   * redundant for free at any depth. Keeping both would put two things in
   * charge of `nominatorIndex` on the same keystroke.
   *
   * 🔴 REFS, NOT STATE, AND NEVER ON THE BOARD. See the note in liveBoard.js:
   * `sanitizeLiveBoard` would drop a history field on reload and `publishBoard`
   * would ship it to every spectator. They can be refs because every mutation
   * pairs with a `setBoard`, so the render that reads their lengths for the
   * button-disabled state is already happening.
   *
   * 🔴 AND THEY ARE READ FROM STORAGE AT MOUNT, which is the whole risk this
   * design has to cover: a laptop sleeping mid-draft is a scenario this product
   * already bends around, and an in-memory-only stack would make Ctrl+Z
   * silently do nothing after a refresh — strictly worse than the depth-one
   * undo it replaces, and worse in the way nobody notices until they need it.
   * ══════════════════════════════════════════════════════════════════════
   */
  const undoRef = useRef(null);
  const redoRef = useRef(null);
  if (undoRef.current === null) {
    const stored = readLiveBoardHistory(activeLeagueName);
    undoRef.current = stored.undo;
    redoRef.current = stored.redo;
  }

  /**
   * The board as of the last commit, so `commitBoard` can snapshot the state it
   * is REPLACING without every one of its ten callers having to hand it over —
   * the same "exactly one place that can forget" argument commitBoard itself
   * rests on. Kept in step on all three paths that can change `board`: here at
   * mount, the league switch below, and commitBoard.
   */
  const boardRef = useRef(board);

  /**
   * ══════════════════════════════════════════════════════════════════════
   * 🔴 THE BOARD FOLLOWS THE LEAGUE. P0 root cause, Aug 12 2026.
   *
   * `board` above is a useState INITIALISER — read once, at mount. So switching
   * the active league left this hook holding the previous league's board (or
   * null) forever, and the Live Board rendered THE WRONG LEAGUE'S STATE with no
   * indication anything was stale.
   *
   * That is what destroyed a five-pick board. Active league was DEFAULT, board
   * was null. Clicking "Open it" in BoardsElsewhere switched the league — and
   * this hook never re-read, so the setup form stayed on screen for a league
   * that already had a board. Filling it in wrote over five picks.
   *
   * ⚠️ NOT ONLY THAT BUTTON. The header's league selector is reachable from the
   * Live Board too and hit the identical bug; it was simply never the path
   * anyone took. Fixing it here fixes every caller, which is why the fix is
   * here and not in the button.
   *
   * 🔴 SET DURING RENDER, DELIBERATELY. This is React's documented pattern for
   * state that must follow a prop — it re-renders immediately without painting
   * the stale value, where an effect would show one frame of the wrong board.
   * On the surface where a wrong board is a data-loss risk, that frame matters.
   *
   * Nothing is written here. Reading a different league never mutates storage.
   * ══════════════════════════════════════════════════════════════════════
   */
  const leagueRef = useRef(activeLeagueName);
  if (leagueRef.current !== activeLeagueName) {
    leagueRef.current = activeLeagueName;
    const next = readLiveBoard(activeLeagueName);
    setBoard(next);
    boardRef.current = next;
    setRestoredCount(next && next.sales.length > 0 ? next.sales.length : 0);
    setStorageFailed(false);
    setSyncOk(null);
    // 🔴 THE HISTORY FOLLOWS THE LEAGUE TOO. Carrying the previous league's
    // stack across would let one Ctrl+Z paste another board's sales onto this
    // one — the same class of cross-league bug the board read above exists to
    // fix, arriving through a door that did not exist when that was written.
    const hist = readLiveBoardHistory(activeLeagueName);
    undoRef.current = hist.undo;
    redoRef.current = hist.redo;
  }

  /**
   * Persist both stacks. Called from inside commitBoard's guarded block only —
   * see the note there on why this must never be able to reach a caller.
   */
  const saveHistory = useCallback((league) => {
    writeLiveBoardHistory(league, { undo: undoRef.current, redo: redoRef.current });
  }, []);

  // The single write path. Everything that mutates the board goes through here
  // so there is exactly one place that can forget to persist.
  //
  // 🔴 ORDER IS THE FEATURE. localStorage first and unconditionally; state
  // second; the network LAST and un-awaited. publishBoard is not async and
  // swallows everything, so an Upstash outage cannot reach this function's
  // caller — the commit handler returns on the localStorage result alone.
  //
  /**
   * ══════════════════════════════════════════════════════════════════════
   * 🔴 `record: false` HAS TWO CALLERS, AND THEY ARE DIFFERENT CLAIMS.
   *
   * 1. undo/redo REPLAYING a snapshot. They have already moved the stacks
   *    themselves, and a commit that re-pushed would make undo an action you
   *    could undo, forever.
   *
   * 2. THE DIALS — setNominator, stepNominator, renameTeam, setNomClock,
   *    setUserTeam. The history records WHAT HAPPENED IN THE AUCTION, not how
   *    the board is configured or what it predicts.
   *
   * ⚠️ (2) IS A DEFECT FIX, FOUND IN REHEARSAL, AND THE SEVERITY IS EVICTION
   * RATHER THAN NOISE. Ben entered three picks, clicked the step arrows a
   * handful of times, then could not undo. His persisted stack was ten entries
   * deep and every one of them was a pointer move — nominatorIndex flipping
   * 8, 9, 8, 9 over an identical 158-sale tail. The undos ran correctly and
   * invisibly while the three picks he wanted back had been pushed off the
   * bottom. On draft night he steps the pointer to correct whose turn it is,
   * which is what that control is FOR, and doing so silently destroyed his
   * ability to undo a mis-entered pick from two minutes earlier: the
   * convenience feature eating the safety feature, exactly when he is busiest.
   *
   * 🔴 EVERY EXCLUDED VERB ALREADY HAS ITS OWN INVERSE. ◀ is what undoes ▶, the
   * menu is what undoes the menu, the editor is what undoes a rename. Putting
   * them in the shared stack duplicated an affordance that already existed and
   * spent a slot that cannot be replaced.
   *
   * ⚠️ IT ALSO SETTLES CHUNK G's TWO "DELIBERATE GAPS": renameTeam and
   * setNomClock used to step the stack without restoring the old name or the
   * old clock. They now do not step it at all, which is the honest version of
   * the same answer.
   *
   * 🔴 THE FLAG IS THE MECHANISM — no second one, and no branching in here on
   * which verb called. The caller knows what it is; commitBoard does not need
   * to guess, and a guess is what would go stale when a verb is added.
   * ══════════════════════════════════════════════════════════════════════
   */
  const commitBoard = useCallback((next, { record = true } = {}) => {
    const prev = boardRef.current;
    const ok = writeLiveBoard(next);
    setStorageFailed(!ok);
    /**
     * ══════════════════════════════════════════════════════════════════════
     * 🔴 THE HISTORY WRITE MUST NEVER BE ABLE TO BREAK THE BOARD WRITE.
     *
     * Same rule `publishBoard` already follows one line below, and for a
     * stronger reason: a quota error on the history key must cost the history
     * and NOTHING else. Losing an entered pick to a failed undo-stack write
     * would be the worst trade available in this file — the board is the
     * product, the stack is a convenience.
     *
     * So it sits AFTER `writeLiveBoard`, inside its own try, and
     * `writeLiveBoardHistory` deliberately does not swallow on its own: one
     * guard, here, where it can be seen next to the write it is protecting.
     * `storageFailed` is NOT set from this — that banner means "your board is
     * not saving", which would be a lie.
     * ══════════════════════════════════════════════════════════════════════
     */
    try {
      if (record && prev) {
        undoRef.current = [...undoRef.current, historySnapshot(prev)].slice(-HISTORY_DEPTH);
        /**
         * 🔴 A NEW ACTION FORKS THE FUTURE. Standard, and the only rule that
         * keeps redo honest: replaying a stack that no longer leads anywhere
         * would paste a board nobody was ever looking at.
         *
         * 🔴 AND IT IS INSIDE THE `record` GUARD ON PURPOSE — the one place the
         * two halves of chunk I interact. Stepping the pointer is not a new
         * action forking anything, so a pending redo has to survive it. Hoist
         * this line out of the guard and undo-step-redo silently stops working,
         * which nothing else in this file would notice.
         */
        redoRef.current = [];
      }
      saveHistory(next?.leagueName);
    } catch { /* the board is written; the stack is a convenience */ }
    setBoard(next);
    boardRef.current = next;
    if (next?.shareId) publishBoard(next.shareId, next, setSyncOk);
    /**
     * 🔴 THE PERMANENT RECORD, AND IT IS A DIFFERENT THING FROM THE PUBLISH
     * ONE LINE ABOVE. The share link is disposable: api/live-board expires it
     * 14 days after the draft. This is the results kept as auction data,
     * upserted under one key per board, and it does NOT extend that TTL.
     *
     * Here, in commitBoard, for the same reason everything else is: this is
     * the single write path, so it is the one place that can log without a
     * caller having to remember to. Sales, edits, deletes and the final seat
     * all arrive through it.
     *
     * 🔴 GATED ON THE SALES ACTUALLY CHANGING. The entry loop is two-stage, so
     * a pick used to flush twice: once for the nomination and once for the
     * sale. The nomination flush carried a record byte-identical to the one
     * before it, because a nomination is not part of the results — and neither
     * is a pointer step, a rename, or a clock change. That doubled the POST
     * volume to say nothing, on a surface whose whole discipline is that
     * telemetry may not cost a keystroke.
     *
     * The comparison is on the SALES ARRAY IDENTITY, not a deep compare: every
     * verb here builds a new board object, but only the sale verbs build a new
     * `sales` array, so `!==` is exact and free. An edit or a delete replaces
     * the array and therefore still logs, which is right — a corrected price
     * is a different result.
     *
     * ⚠️ THE FIRST FLUSH OF A BOARD STILL LOGS. `prev` is null for a board
     * that has just been created, so `prev?.sales` is undefined and the test
     * fires: a room that is set up and then abandoned before pick one is still
     * a record that it happened.
     *
     * ⚠️ AFTER the localStorage write and outside its guard, fire-and-forget,
     * exactly like publishBoard.
     */
    if (next?.shareId && next.sales !== prev?.sales) {
      logLiveBoard(next, { complete: boardIsComplete(next) });
      captureMilestones(prev, next);
    }
    return ok;
  }, [saveHistory]);

  /**
   * Undo the last thing that changed the board — ANY verb, not just a sale.
   *
   * 🔴 UNIFORM ON PURPOSE. Sale, nomination, cancel, edit, delete, rename,
   * step, clock: Ctrl+Z means "undo the last thing I did", and a rule with a
   * carve-out list is a rule nobody can predict at speed. It is also one fewer
   * thing to get wrong at 9pm with eleven people waiting.
   *
   * Returns `{ ok, sale }`. `sale` is the record that left the board when this
   * undo removed exactly one sale from the END — a comparison of two states,
   * not a guess — so the entry row can still name what it undid. It is null for
   * every other verb, and the row says so plainly rather than inventing a name.
   */
  const undo = useCallback(() => {
    const stack = undoRef.current;
    const current = boardRef.current;
    if (!stack.length || !current) return { ok: false, sale: null };
    const snap = stack[stack.length - 1];
    undoRef.current = stack.slice(0, -1);
    redoRef.current = [...redoRef.current, historySnapshot(current)].slice(-HISTORY_DEPTH);
    const next = applyHistorySnapshot(current, snap);
    const ok = commitBoard(next, { record: false });
    return { ok, sale: droppedTailSale(current, next) };
  }, [commitBoard]);

  /** The mirror. Only ever non-empty because an undo put something there. */
  const redo = useCallback(() => {
    const stack = redoRef.current;
    const current = boardRef.current;
    if (!stack.length || !current) return { ok: false, sale: null };
    const snap = stack[stack.length - 1];
    redoRef.current = stack.slice(0, -1);
    undoRef.current = [...undoRef.current, historySnapshot(current)].slice(-HISTORY_DEPTH);
    const next = applyHistorySnapshot(current, snap);
    const ok = commitBoard(next, { record: false });
    return { ok, sale: droppedTailSale(next, current) };
  }, [commitBoard]);

  /**
   * Create the board. `teamNames` is typed in DRAFT ORDER; the user's own seat
   * is just one of them and is not special-cased.
   */
  const startBoard = useCallback((setup, { force = false } = {}) => {
    /**
     * ══════════════════════════════════════════════════════════════════════
     * 🔴 THE OVERWRITE GUARD. P0, Aug 12 2026 — this destroyed a five-pick
     * board of Ben's.
     *
     * Boards are keyed by league name and this function used to write
     * unconditionally, so starting a board for a league that already had one
     * replaced it with no warning and no undo. localStorage has no recycle bin
     * and a live board cannot be regenerated: sixteen slots times ten teams,
     * typed by hand, with eleven people waiting.
     *
     * 🔴 THE GUARD IS HERE, IN THE WRITE PATH, NOT IN THE SETUP SCREEN.
     * The screen is one caller. A guard there protects that button and nothing
     * else; a guard here protects every path that will ever create a board,
     * including ones nobody has written yet. `force` is the caller saying the
     * human has seen the count and chosen anyway.
     *
     * ⚠️ IT READS FROM STORAGE, NOT FROM `board`. The in-memory board can be
     * stale or null for the very league about to be overwritten — that is
     * exactly the state this incident happened in, and checking `board` here
     * would have let it through unchanged.
     * ══════════════════════════════════════════════════════════════════════
     */
    const targetLeague = setup.leagueName || activeLeagueName || 'default';
    if (!force) {
      const existing = readLiveBoard(targetLeague);
      if (existing && existing.sales.length > 0) {
        return {
          ok: false,
          blocked: 'existing-board',
          league: targetLeague,
          leagueName: existing.leagueName || targetLeague,
          picks: existing.sales.length,
        };
      }
    }

    // rosterSize is DERIVED from positions, never stored independently — see
    // the note on positionsForRosterSize. The bench absorbs the difference.
    const positions = positionsForRosterSize(setup.positions, setup.rosterSize);
    const next = {
      leagueName: setup.leagueName || activeLeagueName || 'default',
      startedAt: Date.now(),
      budget: setup.budget,
      teams: setup.teamNames.length,
      rosterSize: setup.rosterSize,
      positions,
      userTeam: Number.isInteger(setup.userTeam) ? setup.userTeam : -1,
      // Minted once, here, and persisted with the board. Every later publish
      // reuses it, so a spectator's link survives every reload of the
      // publisher's tab.
      shareId: newShareId(),
      nomination: null,
      nominatorIndex: 0,
      // ON, at a minute. The two writers of this value must agree: this is
      // the setup path, and sanitizeLiveBoard's default is the recovery path.
      nomClockSec: 60,
      // Optional. Already an absolute instant by the time it reaches here —
      // LiveBoardSetup converts the naive input through epochFromLocalInput, so
      // nothing downstream ever sees a wall clock. null is the normal state.
      draftAt: Number.isFinite(Number(setup.draftAt)) && Number(setup.draftAt) > 0
        ? Number(setup.draftAt)
        : null,
      teamNames: [...setup.teamNames],
      sales: [],
    };
    // Remembered under their own key so Start Over — and next August — still
    // offer them. clearLiveBoard deliberately does not touch this.
    writeSavedTeamNames(next.leagueName, next.teamNames);
    setRestoredCount(0);
    /**
     * 🔴 CLEARED, AND COMMITTED WITHOUT RECORDING. A new board must not inherit
     * a stack: one Ctrl+Z would otherwise restore the PREVIOUS board's sales
     * onto it — the overwrite incident this function already guards against,
     * arriving by a route the guard above cannot see, because the board write
     * itself is perfectly correct.
     *
     * ⚠️ `record: false` IS THE LOAD-BEARING HALF, and clearing alone was not
     * enough — a test caught it. commitBoard pushes the board it is REPLACING,
     * so a plain commit here re-pushed the old board one line after the clear
     * and handed back exactly the undo this was written to remove.
     */
    undoRef.current = [];
    redoRef.current = [];
    clearLiveBoardHistory(next.leagueName);
    const ok = commitBoard(next, { record: false });
    // 🔴 THE FIRST OF THIS SURFACE'S THREE EVENTS, and it fires here — after
    // the write stuck, so a blocked overwrite and a failed write both count as
    // nothing started. The other two (halfway, completed) fire from
    // commitBoard, on a transition. All three carry the room's SHAPE and no
    // name: the league name and the team names are real people. See
    // liveBoardAnalytics.js.
    if (ok) {
      captureLiveBoardStarted({
        teams: next.teams,
        budget: next.budget,
        rosterSize: next.rosterSize,
        isSflex: (positions?.SFLEX || 0) > 0,
      });
    }
    return { ok, blocked: null };
  }, [activeLeagueName, commitBoard]);

  /**
   * Record one sale. THIS is what removes the player from the available board
   * — not blur, not a debounce, not an effect.
   *
   * Returns true when it stuck. The caller clears the row and refocuses the
   * player field on true, and holds everything in place on false.
   */
  const commitSale = useCallback((player, price, teamIndex) => {
    if (!board || !player) return false;
    if (!(teamIndex >= 0 && teamIndex < board.teamNames.length)) return false;
    const sale = saleFromPlayer(player, price, teamIndex, Date.now());
    // The sale clears the block. It does NOT advance the nominator — see
    // `nominate` below for why that has to happen a step earlier.
    const next = { ...board, sales: [...board.sales, sale], nomination: null };
    /**
     * 🔴 …EXCEPT WHEN THE SALE ITSELF FINISHED THE SEAT WE ARE POINTING AT, and
     * that is the COMMON case rather than an edge one. The advance fires at
     * NOMINATION time; a team fills at SALE time. So: seat 3 nominates, the
     * index advances to seat 4, seat 4 then WINS that player and fills his
     * roster — and the stored index is now naming a team that finished a moment
     * ago. A skip applied only in `nominate` never sees this, because by the
     * time the next nomination arrives the board is already announcing the
     * wrong seat to a room reading it.
     *
     * Repaired HERE, on the write, which is what lets `turnSeat` stay a pure
     * read of stored state — see the header on utils/liveBoard.js.
     */
    if (teamIsFull(next, next.nominatorIndex)) {
      const open = nextOpenSeat(next, next.nominatorIndex);
      // ⚠️ NEVER PERSIST -1. sanitizeLiveBoard clamps an out-of-range stored
      // index to 0, so a persisted -1 comes back as seat 0 on the next reload —
      // a silently wrong seat instead of an unchanged one. On a complete board
      // there is no next nominator anyway; boardIsComplete is what the UI reads.
      // 🔴 NOTHING IS RECORDED ABOUT THIS MOVE ANY MORE, and that is the
      // simplification chunk G buys: `undo` restores the whole board state, so
      // the pointer comes back exactly as it was without this write having to
      // describe what it did.
      if (open >= 0) next.nominatorIndex = open;
    }
    return commitBoard(next);
  }, [board, commitBoard]);

  /**
   * Put a player ON THE BLOCK.
   *
   * 🔴 A NOMINATION IS NOT A SALE. It goes nowhere near `sales`, so nothing
   * derived from sales can see it: budgets, rosters, max bid, STILL NEEDED,
   * roomTemp and the cheat sheet's gone state are all unchanged, and the player
   * is still available to be entered. That is structural, not careful — there
   * is no code path from `nomination` into any of them.
   *
   * Nominating over an existing block REPLACES it. The discarded one never
   * became a sale, so there is nothing to undo and nothing to confirm.
   */
  const nominate = useCallback((player) => {
    if (!board || !player?.name || !player?.pos) return false;
    // -1 (nobody has space) leaves the index alone rather than being stored —
    // see the note in commitSale on why a persisted -1 becomes seat 0.
    const open = nextOpenSeat(board, board.nominatorIndex);
    return commitBoard({
      ...board,
      nomination: {
        name: player.name,
        pos: player.pos,
        team: player.team || null,
        sleeper_id: player.sleeper_id != null ? String(player.sleeper_id) : null,
        ts: Date.now(),
      },
      // 🔴 THE ADVANCE BELONGS HERE, NOT ON THE SALE. Advancing when the sale
      // landed meant the prediction pointed at whoever had just nominated for
      // the entire bidding window — you put a player up and the board still
      // said it was your turn. The moment somebody nominates, the question
      // "whose turn is it to nominate" is already answered by the next seat.
      //
      // 🔴 BUT ONLY ONCE PER TURN. A nomination over an existing block REPLACES
      // it (see the header above) — that is one person still taking one turn,
      // not a second turn, so the advance must not fire again. Unconditional,
      // this drifted whose-turn by one every time Ben changed his mind about a
      // name, silently and cumulatively: nothing gates on nominatorIndex, so
      // the board simply started naming the wrong seat to a room reading it.
      //
      // The guard is "was somebody already up", which is exactly right for the
      // cases that must still advance — a sale clears the block, and so does a
      // cancel, so the next nomination after either is a genuine new turn.
      //
      // 🔴 AND IT SKIPS SEATS THAT ARE DONE. A team with no roster space left
      // cannot nominate, so naming it is a prediction that is wrong on its face
      // — and in the last two rounds most of the room is in that state, which is
      // exactly when people are leaning on the board to know whose turn it is.
      nominatorIndex: board.nomination || open < 0 ? board.nominatorIndex : open,
    });
  }, [board, commitBoard]);

  /** He got passed, or the name was wrong. One control, no confirm. */
  const cancelNomination = useCallback(() => {
    if (!board || !board.nomination) return false;
    const n = board.teamNames.length;
    // The walk-back has to move together with the advance above. Cancelling a
    // nomination un-asks the question, so the turn goes back to the seat that
    // nominated — leave it forward and the very first correction desyncs the
    // prediction for the rest of the night.
    //
    // ⚠️ UNCONDITIONAL, AND THAT IS STILL CORRECT AFTER THE REPLACE FIX. However
    // many times the block was replaced, the advance fired exactly once — on
    // the nomination that found the block empty — so exactly one step back
    // returns to the seat that nominated. Cancelling after three changes of
    // mind lands where cancelling a first nomination lands, which is the
    // property a test pins rather than this comment.
    //
    // 🔴 AND IT SKIPS SYMMETRICALLY. The advance walks past full seats, so the
    // walk-back has to as well — step back one raw seat after a skipped advance
    // and the very first correction lands on a team that is done, desyncing the
    // prediction for the rest of the night in the direction nobody checks.
    const back = prevOpenSeat(board, board.nominatorIndex);
    return commitBoard({
      ...board,
      nomination: null,
      nominatorIndex: back >= 0 ? back : ((board.nominatorIndex - 1) % n + n) % n,
    });
  }, [board, commitBoard]);

  /**
   * Correct the whose-turn prediction. ADVISORY ONLY — real auctions skip full
   * teams and drift, and a confidently wrong "next: Rachel" is worse than
   * nothing because people believe it. Nothing gates on this value.
   */
  const setNominator = useCallback((index) => {
    if (!board) return false;
    const n = ((index % board.teamNames.length) + board.teamNames.length) % board.teamNames.length;
    // A dial, not an auction event — see the note on `record` in commitBoard.
    return commitBoard({ ...board, nominatorIndex: n }, { record: false });
  }, [board, commitBoard]);

  /**
   * Step the prediction one seat, either direction, SKIPPING SEATS THAT ARE
   * DONE. The control lives in the pinned entry bar, where Ben's hands already
   * are for all 160 picks.
   *
   * ══════════════════════════════════════════════════════════════════════
   * 🔴 A DIFFERENT VERB FROM `setNominator`, AND THEY MUST NOT BE UNIFIED.
   * They both end up assigning `nominatorIndex`, which is exactly the
   * observation that would destroy them: this one SKIPS full seats, because a
   * step control is most needed in the last two rounds when most of the room is
   * done and a raw ±1 lands on a team that cannot nominate. `setNominator` is
   * EXACT and must stay able to name any seat, full ones included — it is the
   * escape hatch, and liveBoardRotation.test.js pins that it is not filtered.
   * Two tools, two jobs.
   * ══════════════════════════════════════════════════════════════════════
   *
   * ⚠️ RETURNS false AND CHANGES NOTHING when the helper says -1 (nobody
   * anywhere has space). Same rule as `nominate` and `commitSale`: a persisted
   * -1 comes back through sanitizeLiveBoard as seat 0, which is a silently
   * wrong seat rather than an unchanged one.
   */
  const stepNominator = useCallback((dir) => {
    if (!board) return false;
    const to = dir > 0
      ? nextOpenSeat(board, board.nominatorIndex)
      : prevOpenSeat(board, board.nominatorIndex);
    if (!(to >= 0)) return false;
    // 🔴 THE VERB THAT CAUSED THE DEFECT. Ten clicks of this evicted three real
    // picks from a ten-deep stack — see the note on `record` in commitBoard.
    return commitBoard({ ...board, nominatorIndex: to }, { record: false });
  }, [board, commitBoard]);

  /**
   * Correct a sale in place — the fix for a wrong price or a wrong team.
   * Undo only reaches the LAST entry; over 192 picks an error noticed three
   * picks later is guaranteed, so any row is editable.
   */
  const editSale = useCallback((index, patch) => {
    if (!board || !board.sales[index]) return false;
    const sales = board.sales.map((s, i) => (i === index ? { ...s, ...patch } : s));
    return commitBoard({ ...board, sales });
  }, [board, commitBoard]);

  /** Remove any sale, not just the last one. Returns the removed record. */
  const deleteSale = useCallback((index) => {
    if (!board || !board.sales[index]) return null;
    const removed = board.sales[index];
    commitBoard({ ...board, sales: board.sales.filter((_, i) => i !== index) });
    return removed;
  }, [board, commitBoard]);

  /**
   * Rename a team after setup. A typo in a manager's name is a two-second fix,
   * not a reason to start over.
   *
   * 🔴 INDEX-STABLE BY CONSTRUCTION. Sales carry `team` as an INDEX, and
   * `userTeam` is an index too, so replacing the string at a position moves
   * that team's whole history with it and cannot orphan anything. Verified
   * against every downstream consumer: buildLiveTeams, teamNeeds and RosterBoard
   * all key off the index; only display reads the string.
   *
   * 🔴 LIVE BOARD ONLY. This does NOT touch mockTeamConfigs, which is keyed by
   * team NAME behind a strict whitelist — renaming there would silently orphan
   * that seat's archetype, my-guys and keepers, and would also fight the
   * automatic rename calibrated leagues already do. That needs its own design.
   */
  const renameTeam = useCallback((index, rawName) => {
    const name = (rawName || '').trim();
    if (!board || !name || !board.teamNames[index]) return false;
    if (board.teamNames.some((n, i) => i !== index && n.toLowerCase() === name.toLowerCase())) {
      return false;                    // duplicate — the type-ahead could not tell them apart
    }
    const teamNames = board.teamNames.map((n, i) => (i === index ? name : n));
    // Persist to the cross-season key too, so next August offers the CORRECTED
    // name rather than the typo.
    writeSavedTeamNames(board.leagueName, teamNames);
    // Configuration, not an auction event. This also settles chunk G's gap:
    // the stack no longer steps for a rename it was never going to restore.
    return commitBoard({ ...board, teamNames }, { record: false });
  }, [board, commitBoard]);

  /**
   * Length of the nomination clock, in seconds; 0 is off. Board state rather
   * than component state so the published board carries it and every spectator
   * sees the same countdown.
   */
  const setNomClock = useCallback((secs) => {
    if (!board) return false;
    return commitBoard(
      { ...board, nomClockSec: Math.max(0, Math.round(secs) || 0) }, { record: false });
  }, [board, commitBoard]);

  /** Which seat is the user's. -1 means "not saying", and stays legitimate. */
  const setUserTeam = useCallback((index) => {
    if (!board) return false;
    return commitBoard(
      { ...board, userTeam: index === board.userTeam ? -1 : index }, { record: false });
  }, [board, commitBoard]);

  /**
   * Delete a board belonging to ANOTHER league, from the recovery list.
   *
   * 🔴 DELIBERATELY SEPARATE FROM startBoard, AND THAT IS THE POINT. This whole
   * incident was a destructive action that happened without being chosen —
   * fixing it with another one would be the same mistake. Deleting is its own
   * verb, its own button, and its own confirmation naming the pick count.
   *
   * ⚠️ REFUSES THE ACTIVE LEAGUE. That board is `resetBoard`'s job, which also
   * clears the in-memory state; deleting it from here would leave this hook
   * holding a board that no longer exists in storage.
   */
  const deleteBoardFor = useCallback((league) => {
    if (!league || league === (activeLeagueName || 'default')) return false;
    // The board and its history are one thing to a user; an orphaned stack
    // under a deleted league is a board that can come back.
    clearLiveBoardHistory(league);
    return clearLiveBoard(league);
  }, [activeLeagueName]);

  /** Start over. The caller is responsible for the confirm. */
  const resetBoard = useCallback(() => {
    if (board) {
      clearLiveBoard(board.leagueName);
      // Start Over erases the picks; leaving a stack behind would let one
      // Ctrl+Z put all of them back onto a board somebody deliberately erased.
      clearLiveBoardHistory(board.leagueName);
    }
    undoRef.current = [];
    redoRef.current = [];
    setRestoredCount(0);
    setStorageFailed(false);
    setBoard(null);
    boardRef.current = null;
  }, [board]);

  const totals = useMemo(() => (board ? boardTotals(board) : null), [board]);
  const sold = useMemo(() => soldNames(board), [board]);
  // The board views read these. Derived, never stored — a stored copy is one
  // more thing that can disagree with the sales list it came from.
  const liveTeams = useMemo(() => (board ? buildLiveTeams(board) : []), [board]);
  const rosterSlots = useMemo(() => (board ? liveRosterSlots(board) : []), [board]);
  const needs = useMemo(() => (board ? roomNeeds(board) : []), [board]);

  // Defaults for the setup screen, prefilled from the active saved league.
  const setupDefaults = useMemo(() => {
    const teams = settings?.teams ?? 10;
    return {
      leagueName: activeLeagueName || '',
      budget: settings?.budget ?? 200,
      teams,
      rosterSize: rosterSizeOf(settings?.positions),
      positions: settings?.positions || { QB: 1, RB: 2, WR: 3, TE: 1, FLEX: 1, DEF: 1, K: 1 },
      teamNames: suggestedTeamNames(activeLeagueName, teams),
    };
  }, [activeLeagueName, settings?.budget, settings?.teams, settings?.positions]);

  return {
    board, totals, sold, liveTeams, rosterSlots, needs,
    startBoard, commitSale, editSale, deleteSale, setUserTeam, renameTeam, resetBoard,
    deleteBoardFor,
    nominate, cancelNomination, setNominator, stepNominator, setNomClock,
    // Read during render off the refs above; every mutation pairs with a
    // setBoard, so the render that needs them is already happening.
    undo, redo, canUndo: undoRef.current.length > 0, canRedo: redoRef.current.length > 0,
    restoredCount, dismissRestored, storageFailed, syncOk,
    setupDefaults,
  };
}
